<template>
  <div class="page therapy-dashboard">
    <h2>理疗概览</h2>

    <div class="toolbar card">
      <div class="filter-row">
        <div class="filter-item">
          <label>门店</label>
          <select v-model="filters.storeId" @change="loadData">
            <option value="">全部门店</option>
            <option v-for="s in stores" :key="s.id" :value="s.id">{{ s.name }}</option>
          </select>
        </div>
        <div class="filter-item">
          <label>理疗师</label>
          <select v-model="filters.therapistId" @change="loadData">
            <option value="">全部理疗师</option>
            <option v-for="t in therapists" :key="t.id" :value="t.id">{{ t.name }}</option>
          </select>
        </div>
        <div class="filter-item">
          <label>项目</label>
          <select v-model="filters.activityId" @change="loadData">
            <option value="">全部项目</option>
            <option v-for="a in activities" :key="a.id" :value="a.id">{{ a.name }}</option>
          </select>
        </div>
        <div class="filter-item date-range">
          <label>日期范围</label>
          <div class="date-inputs-inline">
            <input type="date" v-model="filters.startDate" @change="loadData" />
            <span class="date-sep">至</span>
            <input type="date" v-model="filters.endDate" @change="loadData" />
          </div>
        </div>
        <div class="filter-item">
          <button class="btn-reset" @click="resetFilters">重置</button>
        </div>
        <div class="filter-item">
          <button class="btn-export" :disabled="exporting || loading" @click="exportBookings">
            {{ exporting ? '导出中...' : '导出订单' }}
          </button>
        </div>
      </div>
      <div class="quick-range">
        <button v-for="r in quickRanges" :key="r.label"
          :class="['btn-range', { active: activeRange === r.label }]"
          @click="applyQuickRange(r)">{{ r.label }}</button>
      </div>
    </div>

    <div v-if="loading" class="loading-state">加载中...</div>
    <div v-else-if="error" class="error-state">{{ error }}</div>
    <template v-else>

      <div class="stats-grid">
        <div class="stat-card" style="--accent: #4F46E5;">
          <div class="stat-label">预约总数</div>
          <div class="stat-value">{{ overview.totalBookings }}</div>
          <div class="stat-change" :class="changeClass(overview.totalBookingsChange)">
            <span v-if="overview.totalBookingsChange !== null && overview.totalBookingsChange !== undefined">
              {{ formatChange(overview.totalBookingsChange) }}
            </span>
          </div>
          <div class="stat-sub">累计 {{ overview.totalBookingsAllTime }}</div>
        </div>
        <div class="stat-card" style="--accent: #10B981;">
          <div class="stat-label">收入金额</div>
          <div class="stat-value">¥{{ formatMoney(overview.totalRevenue) }}</div>
          <div class="stat-change" :class="changeClass(overview.totalRevenueChange)">
            <span v-if="overview.totalRevenueChange !== null && overview.totalRevenueChange !== undefined">
              {{ formatChange(overview.totalRevenueChange) }}
            </span>
          </div>
          <div class="stat-sub">累计 ¥{{ formatMoney(overview.totalRevenueAllTime) }}</div>
        </div>
        <div class="stat-card" style="--accent: #F97316;">
          <div class="stat-label">待付款</div>
          <div class="stat-value">{{ overview.pendingPayment }}</div>
          <div class="stat-change"></div>
          <div class="stat-sub">累计 {{ overview.pendingPaymentAllTime }}</div>
        </div>
        <div class="stat-card" style="--accent: #F59E0B;">
          <div class="stat-label">待审批</div>
          <div class="stat-value">{{ overview.pendingApproval }}</div>
          <div class="stat-change"></div>
          <div class="stat-sub">累计 {{ overview.pendingApprovalAllTime }}</div>
        </div>
        <div class="stat-card" style="--accent: #06B6D4;">
          <div class="stat-label">待核销</div>
          <div class="stat-value">{{ overview.pendingVerify }}</div>
          <div class="stat-change"></div>
          <div class="stat-sub">累计 {{ overview.pendingVerifyAllTime }}</div>
        </div>
        <div class="stat-card" style="--accent: #3B82F6;">
          <div class="stat-label">已完成</div>
          <div class="stat-value">{{ overview.completed }}</div>
          <div class="stat-change"></div>
          <div class="stat-sub">累计 {{ overview.completedAllTime }}</div>
        </div>
        <div class="stat-card" style="--accent: #EF4444;">
          <div class="stat-label">已取消</div>
          <div class="stat-value">{{ overview.cancelled }}</div>
          <div class="stat-change" :class="changeClass(overview.cancelledChange, true)">
            <span v-if="overview.cancelledChange !== null && overview.cancelledChange !== undefined">
              {{ formatChange(overview.cancelledChange) }}
            </span>
          </div>
          <div class="stat-sub">累计 {{ overview.cancelledAllTime }}</div>
        </div>
        <div class="stat-card" style="--accent: #6366F1;">
          <div class="stat-label">到店率</div>
          <div class="stat-value">{{ overview.checkInRate }}%</div>
          <div class="stat-change" :class="changeClass(overview.checkInRateChange)">
            <span v-if="overview.checkInRateChange !== null && overview.checkInRateChange !== undefined">
              {{ formatChange(overview.checkInRateChange) }}
            </span>
          </div>
          <div class="stat-sub">&nbsp;</div>
        </div>
      </div>

      <div class="todo-workbench-section card">
        <div class="todo-workbench-section-header">
          <h3>当前全部待办</h3>
          <p>以下为所有日期的待办数量（不受上方日期范围影响），点击卡片打开工作台直接处理</p>
        </div>
        <div class="todo-workbench-cards">
          <div class="todo-workbench-card" style="--accent: #F97316;" @click="openTodoWorkbench('payment')">
            <div class="todo-workbench-card-label">待付款</div>
            <div class="todo-workbench-card-value">{{ overview.pendingPaymentAllTime }}</div>
            <div class="todo-workbench-card-hint">点击处理</div>
          </div>
          <div class="todo-workbench-card todo-workbench-card-approval" style="--accent: #F59E0B;" @click="openTodoWorkbench('approval')">
            <div class="todo-workbench-card-label">待审批</div>
            <div class="todo-workbench-card-value">{{ overview.pendingApprovalAllTime }}</div>
            <div class="todo-workbench-card-hint">点击处理</div>
          </div>
          <div class="todo-workbench-card" style="--accent: #06B6D4;" @click="openTodoWorkbench('verify')">
            <div class="todo-workbench-card-label">待核销</div>
            <div class="todo-workbench-card-value">{{ overview.pendingVerifyAllTime }}</div>
            <div class="todo-workbench-card-hint">点击处理</div>
          </div>
          <div class="todo-workbench-card" style="--accent: #8B5CF6;" @click="openTodoWorkbench('bundlePayment')">
            <div class="todo-workbench-card-label">套餐待确认收款</div>
            <div class="todo-workbench-card-value">{{ overview.pendingBundlePaymentAllTime }}</div>
            <div class="todo-workbench-card-hint">点击处理</div>
          </div>
        </div>
      </div>

      <div class="charts-row">
        <div class="chart-card card">
          <div class="chart-header">
            <h3>收入与预约趋势</h3>
          </div>
          <div ref="trendChart" class="chart-container"></div>
        </div>
        <div class="chart-card card">
          <div class="chart-header">
            <h3>预约分布</h3>
            <div class="chart-tabs">
              <button :class="['btn-tab', { active: distTab === 'activity' }]" @click="distTab = 'activity'; updateDistChart()">按项目</button>
              <button :class="['btn-tab', { active: distTab === 'therapist' }]" @click="distTab = 'therapist'; updateDistChart()">按理疗师</button>
            </div>
          </div>
          <div ref="distChart" class="chart-container"></div>
        </div>
      </div>

      <div class="action-row">
        <div class="quick-actions card">
          <h3>快速操作</h3>
          <div class="action-grid">
            <button class="action-btn" @click="$router.push('/dashboard/therapy-bookings')">预约管理</button>
            <button class="action-btn" @click="$router.push('/dashboard/therapy-bundle-purchases')">套餐订单管理</button>
            <button class="action-btn" @click="$router.push('/dashboard/therapy-schedules')">排班管理</button>
            <button class="action-btn" @click="$router.push('/dashboard/therapy-activities')">活动管理</button>
            <button class="action-btn" @click="$router.push('/dashboard/therapy-customers')">客户管理</button>
          </div>
        </div>
      </div>

    </template>

    <div v-if="showTodoWorkbench" class="dialog-mask">
      <div class="dialog dialog-wide todo-workbench-dialog" :class="`todo-workbench-${todoWorkbenchType}`">
        <div class="dialog-header">
          <div>
            <h3>{{ todoWorkbenchTitle }}</h3>
            <p class="todo-workbench-subtitle">当前全部待办 {{ todoWorkbenchAllTimeCount }} 单 · 不受上方日期范围影响</p>
          </div>
          <button class="dialog-close" :disabled="loading" @click="closeTodoWorkbench">×</button>
        </div>

        <div v-if="todoWorkbenchType === 'approval' && todoWorkbenchList.length > 0" class="batch-toolbar">
          <div class="selection-count">已选 {{ selectedPendingApprovalIds.length }} 单</div>
          <div class="batch-actions">
            <button class="btn-batch btn-batch-light" :disabled="loading" @click="selectAllPendingApprovals">全选当前页</button>
            <button class="btn-batch btn-batch-light" :disabled="loading || selectedPendingApprovalIds.length === 0" @click="clearPendingApprovalSelection">清空选择</button>
            <button class="btn-batch btn-batch-primary" :disabled="!canBatchApprove" @click="openBatchApprove">批量通过审批</button>
          </div>
        </div>

        <div class="todo-workbench-list">
          <div v-if="todoWorkbenchList.length === 0" class="empty-text">{{ todoWorkbenchEmptyText }}</div>

          <div v-else-if="todoWorkbenchType === 'payment'" class="pending-list">
            <div v-for="item in todoWorkbenchList" :key="item.id" class="pending-item pending-workbench-item">
              <div class="pending-info">
                <span class="pending-name">{{ item.contactName }}</span>
                <span class="pending-activity">{{ item.activityName }}</span>
                <span class="pending-time">{{ item.bookingDate }} {{ formatTimeRange(item) }}</span>
              </div>
              <div class="pending-meta">
                <span class="pending-date">{{ item.bookingDate }}</span>
                <span class="pending-amount">¥{{ formatMoney(item.amount) }}</span>
              </div>
              <div class="pending-row-actions">
                <button class="btn-row btn-row-primary" :disabled="loading" @click="confirmPayment(item)">确认收款</button>
                <button class="btn-row btn-row-danger" :disabled="loading" @click="openSingleDecision('cancel', item)">取消预约</button>
              </div>
            </div>
          </div>

          <div v-else-if="todoWorkbenchType === 'approval'" class="pending-list">
            <div v-for="item in todoWorkbenchList" :key="item.id" class="pending-item pending-approval-item" :class="{ selected: selectedPendingApprovalIds.includes(item.id) }">
              <label class="pending-select" @click.stop>
                <input
                  type="checkbox"
                  :checked="selectedPendingApprovalIds.includes(item.id)"
                  :disabled="loading"
                  @change="togglePendingSelection(item.id)"
                />
              </label>
              <div class="pending-info" @click="togglePendingSelection(item.id)">
                <span class="pending-name">{{ item.contactName }}</span>
                <span class="pending-activity">{{ item.activityName }}</span>
                <span class="pending-time">{{ item.bookingDate }} {{ formatTimeRange(item) }}</span>
                <span v-if="item.therapistName" class="pending-therapist">{{ item.therapistName }}</span>
              </div>
              <div class="pending-meta">
                <span class="pending-date">{{ item.bookingDate }}</span>
                <span class="pending-amount">¥{{ formatMoney(item.amount) }}</span>
              </div>
              <div class="pending-row-actions" @click.stop>
                <button class="btn-row btn-row-primary" :disabled="loading" @click="singleApprove(item)">通过审批</button>
                <button class="btn-row" :disabled="loading" @click="openSingleDecision('reject', item)">拒绝</button>
                <button class="btn-row btn-row-danger" :disabled="loading" @click="openSingleDecision('cancel', item)">取消预约</button>
              </div>
            </div>
          </div>

          <div v-else-if="todoWorkbenchType === 'verify'" class="pending-list">
            <div v-for="item in todoWorkbenchList" :key="item.id" class="pending-item pending-workbench-item">
              <div class="pending-info">
                <span class="pending-name">{{ item.contactName }}</span>
                <span class="pending-activity">{{ item.activityName }}</span>
                <span class="pending-time">{{ item.bookingDate }} {{ formatTimeRange(item) }}</span>
                <span v-if="item.therapistName" class="pending-therapist">{{ item.therapistName }}</span>
              </div>
              <div class="pending-meta">
                <span class="pending-date">{{ item.bookingDate }}</span>
                <span class="pending-amount">¥{{ formatMoney(item.amount) }}</span>
              </div>
              <div class="pending-row-actions">
                <button class="btn-row btn-row-primary" :disabled="loading" @click="verifyBooking(item)">核销</button>
              </div>
            </div>
          </div>

          <div v-else-if="todoWorkbenchType === 'bundlePayment'" class="pending-list">
            <div v-for="item in todoWorkbenchList" :key="item.id" class="pending-item pending-workbench-item">
              <div class="pending-info">
                <span class="pending-name">{{ item.contactPhone || '—' }}</span>
                <span class="pending-activity">{{ item.bundleName }}</span>
                <span class="pending-time">订单号 {{ item.purchaseNo }}</span>
              </div>
              <div class="pending-meta">
                <span class="pending-date">{{ formatBundleCreatedAt(item.createdAt) }}</span>
                <span class="pending-amount">¥{{ formatMoney(item.amount) }}</span>
              </div>
              <div class="pending-row-actions">
                <button class="btn-row btn-row-primary" :disabled="loading" @click="confirmBundlePayment(item)">确认收款</button>
                <button class="btn-row btn-row-danger" :disabled="loading" @click="cancelBundlePurchase(item)">取消订单</button>
              </div>
            </div>
          </div>
        </div>

        <button v-if="todoWorkbenchAllTimeCount > todoWorkbenchList.length" class="btn-view-all" @click="goToBookingFromWorkbench">查看全部 →</button>

        <div class="dialog-actions">
          <button class="btn-batch btn-batch-light" :disabled="loading" @click="closeTodoWorkbench">关闭</button>
        </div>
      </div>
    </div>

    <div v-if="showBatchDialog" class="dialog-mask">
      <div class="dialog dialog-wide">
        <div class="dialog-header">
          <h3>批量通过审批预览</h3>
          <button class="dialog-close" :disabled="loading" @click="closeBatchDialog">×</button>
        </div>
        <textarea v-model="batchNotes" class="dialog-notes" placeholder="审批备注（可选）"></textarea>
        <div v-if="batchPreview" class="batch-summary">
          <span>共 {{ batchPreview.summary?.total || 0 }} 单</span>
          <span class="summary-success">可通过 {{ batchPreview.summary?.approvable || 0 }} 单</span>
          <span class="summary-failed">失败 {{ batchPreview.summary?.failed || 0 }} 单</span>
        </div>
        <div v-if="batchPreview" class="preview-groups">
          <div class="preview-group">
            <h4>可通过</h4>
            <div v-if="approvablePreviewItems.length === 0" class="preview-empty">暂无可审批预约</div>
            <div v-for="item in approvablePreviewItems" :key="item.bookingId" class="preview-row">
              <div>
                <strong>{{ item.contactName }}</strong>
                <span>{{ item.activityName }}</span>
              </div>
              <div class="preview-meta">
                <span>{{ item.bookingDate }} {{ item.startTime }}-{{ item.endTime }}</span>
                <span class="room-name">{{ item.roomName || '自动分房' }}</span>
              </div>
            </div>
          </div>
          <div class="preview-group">
            <h4>失败项</h4>
            <div v-if="failedPreviewItems.length === 0" class="preview-empty">暂无失败项</div>
            <div v-for="item in failedPreviewItems" :key="item.bookingId" class="preview-row preview-row-failed">
              <div>
                <strong>{{ item.contactName }}</strong>
                <span>{{ item.activityName }}</span>
              </div>
              <div class="preview-meta">
                <span>{{ item.bookingDate }} {{ item.startTime }}-{{ item.endTime }}</span>
                <span class="failed-reason">{{ item.reason || '不可审批' }}</span>
              </div>
            </div>
          </div>
        </div>
        <div class="dialog-actions">
          <button class="btn-batch btn-batch-light" :disabled="loading" @click="closeBatchDialog">取消</button>
          <button class="btn-batch btn-batch-primary" :disabled="loading || !batchPreview || (batchPreview.summary?.approvable || 0) === 0" @click="submitBatchApprove">确认通过</button>
        </div>
      </div>
    </div>

    <div v-if="singleDecision" class="dialog-mask">
      <div class="dialog">
        <div class="dialog-header">
          <h3>{{ singleDecision.action === 'reject' ? '拒绝预约' : '取消预约' }}</h3>
          <button class="dialog-close" :disabled="loading" @click="singleDecision = null">×</button>
        </div>
        <div class="decision-summary">
          <p>{{ singleDecision.booking.contactName }} · {{ singleDecision.booking.activityName }}</p>
          <p>{{ singleDecision.booking.bookingDate }} {{ formatTimeRange(singleDecision.booking) }}</p>
        </div>
        <textarea v-model="singleReason" class="dialog-notes" :placeholder="singleDecision.action === 'reject' ? '请输入拒绝原因' : '请输入取消原因'"></textarea>
        <div class="dialog-actions">
          <button class="btn-batch btn-batch-light" :disabled="loading" @click="singleDecision = null">取消</button>
          <button class="btn-batch btn-batch-primary" :disabled="loading || !singleReason.trim()" @click="submitSingleDecision">确定</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import { therapyDashboardService } from '@/services/therapyDashboardService'
import { therapyBookingService } from '@/services/therapyBookingService'
import { therapyBundleService } from '@/services/therapyBundleService'

export default {
  name: 'TherapyDashboard',
  data() {
    return {
      loading: false,
      exporting: false,
      error: null,
      stores: [],
      therapists: [],
      activities: [],
      filters: {
        storeId: '',
        therapistId: '',
        activityId: '',
        startDate: '',
        endDate: ''
      },
      activeRange: '近7天',
      quickRanges: [
        { label: '近7天', days: 7 },
        { label: '近30天', days: 30 },
        { label: '近90天', days: 90 }
      ],
      overview: {
        totalBookings: 0,
        totalBookingsChange: null,
        totalBookingsAllTime: 0,
        totalRevenue: 0,
        totalRevenueChange: null,
        totalRevenueAllTime: 0,
        pendingPayment: 0,
        pendingPaymentAllTime: 0,
        pendingApproval: 0,
        pendingApprovalAllTime: 0,
        pendingVerify: 0,
        pendingVerifyAllTime: 0,
        pendingBundlePayment: 0,
        pendingBundlePaymentAllTime: 0,
        completed: 0,
        completedAllTime: 0,
        cancelled: 0,
        cancelledChange: null,
        cancelledAllTime: 0,
        checkInRate: 0,
        checkInRateChange: null
      },
      trend: [],
      distributionByActivity: [],
      distributionByTherapist: [],
      pendingBookings: [],
      pendingPaymentBookings: [],
      pendingVerifyBookings: [],
      pendingBundlePurchases: [],
      distTab: 'activity',
      selectedPendingApprovalIds: [],
      previewBookingIds: [],
      batchApproveMode: 'batch',
      batchPreview: null,
      showBatchDialog: false,
      batchNotes: '',
      singleDecision: null,
      singleReason: '',
      showTodoWorkbench: false,
      todoWorkbenchType: null
    }
  },
  computed: {
    canBatchApprove() {
      return this.selectedPendingApprovalIds.length > 0 && !this.loading
    },
    approvablePreviewItems() {
      return (this.batchPreview?.items || []).filter(item => item.approvable)
    },
    failedPreviewItems() {
      return (this.batchPreview?.items || []).filter(item => !item.approvable)
    },
    todoWorkbenchTitle() {
      const titles = {
        payment: '待付款工作台',
        approval: '待审批工作台',
        verify: '待核销工作台',
        bundlePayment: '套餐待确认收款'
      }
      return titles[this.todoWorkbenchType] || ''
    },
    todoWorkbenchAllTimeCount() {
      const counts = {
        payment: this.overview.pendingPaymentAllTime,
        approval: this.overview.pendingApprovalAllTime,
        verify: this.overview.pendingVerifyAllTime,
        bundlePayment: this.overview.pendingBundlePaymentAllTime
      }
      return counts[this.todoWorkbenchType] || 0
    },
    todoWorkbenchList() {
      const lists = {
        payment: this.pendingPaymentBookings,
        approval: this.pendingBookings,
        verify: this.pendingVerifyBookings,
        bundlePayment: this.pendingBundlePurchases
      }
      return lists[this.todoWorkbenchType] || []
    },
    todoWorkbenchStatus() {
      const statuses = {
        payment: 'PENDING_PAYMENT',
        approval: 'PAID',
        verify: 'CONFIRMED'
      }
      return statuses[this.todoWorkbenchType] || ''
    },
    todoWorkbenchEmptyText() {
      const texts = {
        payment: '暂无待付款预约',
        approval: '暂无待审批预约',
        verify: '暂无待核销预约',
        bundlePayment: '暂无待确认收款的套餐订单'
      }
      return texts[this.todoWorkbenchType] || '暂无待办'
    }
  },
  mounted() {
    this.applyQuickRange(this.quickRanges[0])
    this.loadFilterOptions()
  },
  beforeUnmount() {
    if (this.trendChartInstance) {
      this.trendChartInstance.dispose()
    }
    if (this.distChartInstance) {
      this.distChartInstance.dispose()
    }
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    async loadFilterOptions() {
      try {
        const [storesRes, therapistsRes, activitiesRes] = await Promise.all([
          therapyDashboardService.getStoreList(),
          therapyDashboardService.getTherapistList({ status: 'ACTIVE' }),
          therapyDashboardService.getActivityList({ status: 'PUBLISHED' })
        ])
        this.stores = storesRes.data?.content || storesRes.data || []
        if (!Array.isArray(this.stores)) this.stores = this.stores ? [this.stores] : []
        this.therapists = therapistsRes.data?.content || []
        this.activities = activitiesRes.data?.content || []
      } catch (e) {
        console.error('加载筛选选项失败:', e)
      }
    },
    applyQuickRange(range) {
      this.activeRange = range.label
      const end = new Date()
      const start = new Date()
      start.setDate(start.getDate() - range.days + 1)
      this.filters.startDate = this.formatDate(start)
      this.filters.endDate = this.formatDate(end)
      this.loadData()
    },
    resetFilters() {
      this.filters.storeId = ''
      this.filters.therapistId = ''
      this.filters.activityId = ''
      this.activeRange = '近7天'
      this.applyQuickRange(this.quickRanges[0])
    },
    async exportBookings() {
      if (!this.filters.startDate || !this.filters.endDate) {
        alert('请选择日期范围')
        return
      }
      this.exporting = true
      try {
        const params = {}
        if (this.filters.storeId) params.storeId = this.filters.storeId
        if (this.filters.therapistId) params.therapistId = this.filters.therapistId
        if (this.filters.activityId) params.activityId = this.filters.activityId
        params.startDate = this.filters.startDate
        params.endDate = this.filters.endDate
        await therapyDashboardService.exportBookings(params)
      } catch (e) {
        const msg = e?.message
          || e?.response?.data?.message
          || e?.response?.data?.error
          || '导出失败，请重试'
        alert(msg)
        console.error('导出订单失败:', e)
      } finally {
        this.exporting = false
      }
    },
    async loadData(options = {}) {
      const silent = options.silent === true
      if (!silent) {
        this.loading = true
        this.error = null
      }
      try {
        const params = {}
        if (this.filters.storeId) params.storeId = this.filters.storeId
        if (this.filters.therapistId) params.therapistId = this.filters.therapistId
        if (this.filters.activityId) params.activityId = this.filters.activityId
        params.startDate = this.filters.startDate
        params.endDate = this.filters.endDate

        const res = await therapyDashboardService.getDashboardData(params)
        const data = res.data
        if (data) {
          this.overview = data.overview || this.overview
          this.trend = data.trend || []
          this.distributionByActivity = data.distributionByActivity || []
          this.distributionByTherapist = data.distributionByTherapist || []
          this.pendingBookings = data.pendingBookings || []
          this.pendingPaymentBookings = data.pendingPaymentBookings || []
          this.pendingVerifyBookings = data.pendingVerifyBookings || []
          this.pendingBundlePurchases = data.pendingBundlePurchases || []
          const pendingIds = new Set(this.pendingBookings.map(item => item.id))
          this.selectedPendingApprovalIds = this.selectedPendingApprovalIds.filter(id => pendingIds.has(id))
        }
      } catch (e) {
        this.error = e?.response?.data?.error || e?.message || '加载概览数据失败'
        console.error('加载概览数据失败:', e)
      } finally {
        if (!silent) {
          this.loading = false
        }
        this.$nextTick(() => {
          this.initCharts()
          this.updateTrendChart()
          this.updateDistChart()
        })
      }
    },
    initCharts() {
      if (this.trendChartInstance) {
        this.trendChartInstance.dispose()
      }
      if (this.distChartInstance) {
        this.distChartInstance.dispose()
      }
      this.trendChartInstance = echarts.init(this.$refs.trendChart)
      this.distChartInstance = echarts.init(this.$refs.distChart)
      window.removeEventListener('resize', this.handleResize)
      window.addEventListener('resize', this.handleResize)
    },
    handleResize() {
      this.trendChartInstance?.resize()
      this.distChartInstance?.resize()
    },
    updateTrendChart() {
      if (!this.trendChartInstance) return
      const dates = this.trend.map(item => {
        const d = item.date
        return d.substring(5)
      })
      const revenues = this.trend.map(item => item.revenue)
      const bookings = this.trend.map(item => item.bookings)

      this.trendChartInstance.setOption({
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'cross' }
        },
        legend: {
          data: ['收入', '预约数'],
          bottom: 0
        },
        grid: {
          top: 30, right: 60, bottom: 40, left: 60
        },
        xAxis: {
          type: 'category',
          data: dates,
          axisLabel: { fontSize: 11 }
        },
        yAxis: [
          {
            type: 'value',
            name: '收入(元)',
            position: 'left'
          },
          {
            type: 'value',
            name: '预约数',
            position: 'right'
          }
        ],
        series: [
          {
            name: '收入',
            type: 'line',
            data: revenues,
            smooth: true,
            yAxisIndex: 0,
            itemStyle: { color: '#10B981' },
            areaStyle: {
              color: {
                type: 'linear',
                x: 0, y: 0, x2: 0, y2: 1,
                colorStops: [
                  { offset: 0, color: 'rgba(16,185,129,0.3)' },
                  { offset: 1, color: 'rgba(16,185,129,0.05)' }
                ]
              }
            }
          },
          {
            name: '预约数',
            type: 'line',
            data: bookings,
            smooth: true,
            yAxisIndex: 1,
            itemStyle: { color: '#4F46E5' },
            lineStyle: { type: 'dashed' }
          }
        ]
      })
    },
    updateDistChart() {
      if (!this.distChartInstance) return
      const distData = this.distTab === 'activity' ? this.distributionByActivity : this.distributionByTherapist
      const total = distData.reduce((sum, item) => sum + item.value, 0)

      this.distChartInstance.setOption({
        tooltip: {
          trigger: 'item',
          formatter: '{b}: {c} ({d}%)'
        },
        legend: {
          type: 'scroll',
          bottom: 0,
          textStyle: { fontSize: 12 }
        },
        series: [
          {
            type: 'pie',
            radius: ['35%', '60%'],
            center: ['50%', '45%'],
            data: distData,
            label: {
              show: false
            },
            emphasis: {
              label: {
                show: true,
                fontWeight: 'bold'
              }
            },
            itemStyle: {
              borderColor: '#fff',
              borderWidth: 2
            }
          }
        ],
        graphic: total > 0 ? [{
          type: 'text',
          left: 'center',
          top: '38%',
          style: {
            text: `${total}`,
            fontSize: 20,
            fontWeight: 'bold',
            textAlign: 'center',
            fill: '#333'
          }
        }, {
          type: 'text',
          left: 'center',
          top: '48%',
          style: {
            text: '总计',
            fontSize: 12,
            textAlign: 'center',
            fill: '#999'
          }
        }] : []
      })
    },
    formatDate(date) {
      const y = date.getFullYear()
      const m = String(date.getMonth() + 1).padStart(2, '0')
      const d = String(date.getDate()).padStart(2, '0')
      return `${y}-${m}-${d}`
    },
    formatMoney(val) {
      if (val === null || val === undefined) return '0'
      const num = typeof val === 'string' ? parseFloat(val) : val
      if (isNaN(num)) return '0'
      if (num >= 10000) {
        return (num / 10000).toFixed(1) + '万'
      }
      return num.toFixed(2).replace(/\.?0+$/, '').replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    },
    formatTimeRange(item) {
      if (item.startTime && item.endTime) return `${item.startTime}-${item.endTime}`
      return item.startTime || item.endTime || ''
    },
    formatChange(val) {
      if (val === null || val === undefined) return ''
      const prefix = val > 0 ? '↑' : (val < 0 ? '↓' : '')
      return `${prefix}${Math.abs(val)}%`
    },
    changeClass(val, inverse = false) {
      if (val === null || val === undefined) return ''
      if (inverse) {
        return val < 0 ? 'change-good' : (val > 0 ? 'change-bad' : '')
      }
      return val > 0 ? 'change-good' : (val < 0 ? 'change-bad' : '')
    },
    goToBooking(status) {
      const query = status ? { status } : {}
      this.$router.push({ path: '/dashboard/therapy-bookings', query })
    },
    async openTodoWorkbench(type) {
      this.todoWorkbenchType = type
      this.showTodoWorkbench = true
      if (type === 'approval') {
        this.selectedPendingApprovalIds = []
      }
      await this.loadData({ silent: true })
    },
    closeTodoWorkbench() {
      if (this.loading) return
      this.showTodoWorkbench = false
      this.todoWorkbenchType = null
    },
    goToBookingFromWorkbench() {
      if (this.todoWorkbenchType === 'bundlePayment') {
        this.closeTodoWorkbench()
        this.$router.push({ path: '/dashboard/therapy-bundle-purchases', query: { status: 'PENDING_PAYMENT' } })
        return
      }
      const status = this.todoWorkbenchStatus
      this.closeTodoWorkbench()
      this.goToBooking(status)
    },
    formatBundleCreatedAt(iso) {
      if (!iso) return '—'
      return String(iso).replace('T', ' ').slice(0, 16)
    },
    async confirmBundlePayment(item) {
      if (this.loading || !item?.id) return
      if (!confirm('确认已收到该套餐款项？确认后将生成可用次数。')) return
      this.loading = true
      try {
        await therapyBundleService.confirmPurchasePayment(item.id)
        await this.loadData({ silent: true })
      } catch (e) {
        alert(e?.response?.data?.error || '确认收款失败')
      } finally {
        this.loading = false
      }
    },
    async cancelBundlePurchase(item) {
      if (this.loading || !item?.id) return
      if (!confirm('确定取消该套餐订单？')) return
      this.loading = true
      try {
        await therapyBundleService.cancelPurchase(item.id)
        await this.loadData({ silent: true })
      } catch (e) {
        alert(e?.response?.data?.error || '取消失败')
      } finally {
        this.loading = false
      }
    },
    async confirmPayment(item) {
      if (this.loading || !item?.id) return
      this.loading = true
      try {
        await therapyBookingService.confirmPayment(item.id)
        await this.loadData({ silent: true })
      } catch (e) {
        alert(e?.response?.data?.error || '确认收款失败')
      } finally {
        this.loading = false
      }
    },
    async verifyBooking(item) {
      if (this.loading || !item?.id) return
      this.loading = true
      try {
        await therapyBookingService.verify(item.id)
        await this.loadData({ silent: true })
      } catch (e) {
        alert(e?.response?.data?.error || '核销失败')
      } finally {
        this.loading = false
      }
    },
    togglePendingSelection(id) {
      if (this.loading) return
      const index = this.selectedPendingApprovalIds.indexOf(id)
      if (index >= 0) {
        this.selectedPendingApprovalIds.splice(index, 1)
      } else {
        this.selectedPendingApprovalIds.push(id)
      }
    },
    selectAllPendingApprovals() {
      this.selectedPendingApprovalIds = this.pendingBookings.map(item => item.id)
    },
    clearPendingApprovalSelection() {
      this.selectedPendingApprovalIds = []
    },
    async openBatchApprove() {
      if (this.loading || !this.canBatchApprove) return
      await this.openApprovePreview([...this.selectedPendingApprovalIds], 'batch')
    },
    async openApprovePreview(bookingIds, mode) {
      if (this.loading || bookingIds.length === 0) return
      this.loading = true
      this.previewBookingIds = [...bookingIds]
      this.batchApproveMode = mode
      try {
        const res = await therapyBookingService.previewBatchApprove({
          bookingIds: this.previewBookingIds,
          confirmationNotes: this.batchNotes
        })
        this.batchPreview = res.data
        this.showBatchDialog = true
      } catch (e) {
        this.resetBatchPreview()
        alert(e?.response?.data?.error || '批量审批预览失败')
      } finally {
        this.loading = false
      }
    },
    async submitBatchApprove() {
      if (this.loading || !this.batchPreview || (this.batchPreview.summary?.approvable || 0) === 0 || this.previewBookingIds.length === 0) return
      this.loading = true
      try {
        const bookingIds = (this.batchPreview.items || [])
          .filter(item => item.approvable)
          .map(item => item.bookingId)
        if (bookingIds.length === 0) {
          alert('当前预览没有可通过的预约')
          return
        }
        const mode = this.batchApproveMode
        const res = await therapyBookingService.batchApprove({
          bookingIds,
          confirmationNotes: this.batchNotes
        })
        const summary = res.data?.summary || {}
        if (mode === 'batch') {
          this.selectedPendingApprovalIds = []
        }
        this.resetBatchPreview()
        alert(`成功 ${summary.approvable || 0} 单，失败 ${summary.failed || 0} 单`)
        await this.loadData({ silent: this.showTodoWorkbench })
      } catch (e) {
        alert(e?.response?.data?.error || '批量通过审批失败')
      } finally {
        this.loading = false
      }
    },
    async singleApprove(item) {
      if (this.loading) return
      await this.openApprovePreview([item.id], 'single')
    },
    closeBatchDialog() {
      if (this.loading) return
      this.resetBatchPreview()
    },
    resetBatchPreview() {
      this.batchPreview = null
      this.showBatchDialog = false
      this.previewBookingIds = []
      this.batchApproveMode = 'batch'
    },
    openSingleDecision(action, booking) {
      this.singleDecision = { action, booking }
      this.singleReason = ''
    },
    async submitSingleDecision() {
      const reason = this.singleReason.trim()
      if (this.loading || !reason || !this.singleDecision) return
      this.loading = true
      try {
        const { action, booking } = this.singleDecision
        if (action === 'reject') {
          await therapyBookingService.reject(booking.id, reason)
        } else {
          await therapyBookingService.cancel(booking.id, reason)
        }
        this.singleDecision = null
        this.singleReason = ''
        await this.loadData({ silent: this.showTodoWorkbench })
      } catch (e) {
        alert(e?.response?.data?.error || '处理预约失败')
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.therapy-dashboard {
  padding: 0;
}

.therapy-dashboard > h2 {
  padding: 0 0 16px 0;
}

.filter-row {
  display: flex;
  align-items: flex-end;
  gap: 16px;
  flex-wrap: wrap;
}

.filter-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.filter-item label {
  font-size: 12px;
  color: var(--text-muted);
  font-weight: 500;
}

.filter-item select,
.date-inputs-inline input[type="date"] {
  height: 40px;
  box-sizing: border-box;
  padding: 0 12px;
  font-size: 13px;
  line-height: 1.25;
  min-width: 120px;
}

.date-range {
  flex: 1;
  min-width: 260px;
}

.date-inputs-inline {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 40px;
}

.date-inputs-inline input[type="date"] {
  min-width: 140px;
}

.date-sep {
  color: var(--text-muted);
  font-size: 13px;
  line-height: 40px;
}

.btn-reset {
  background: #fff;
  color: var(--text-main);
  border: 1px solid var(--border-color);
  height: 40px;
  box-sizing: border-box;
  padding: 0 16px;
  font-size: 13px;
}

.btn-reset:hover {
  background: var(--primary-light);
  border-color: var(--primary);
  color: var(--primary);
}

.btn-export {
  background: var(--primary);
  color: #fff;
  border: 1px solid var(--primary);
  height: 40px;
  box-sizing: border-box;
  padding: 0 16px;
  font-size: 13px;
}

.btn-export:hover:not(:disabled) {
  opacity: 0.92;
}

.btn-export:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.quick-range {
  display: flex;
  gap: 8px;
  margin-top: 12px;
}

.btn-range {
  background: #fff;
  color: var(--text-muted);
  border: 1px solid var(--border-color);
  padding: 5px 14px;
  font-size: 12px;
  border-radius: var(--radius-sm);
}

.btn-range:hover {
  color: var(--primary);
  border-color: var(--primary);
}

.btn-range.active {
  background: var(--primary);
  color: #fff;
  border-color: var(--primary);
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 24px;
}

.todo-workbench-section {
  margin-bottom: 24px;
  padding: 20px;
  background: linear-gradient(180deg, #FFFBEB 0%, var(--surface-card) 42%);
  border-color: #FCD34D;
}

.todo-workbench-section-header {
  margin-bottom: 16px;
}

.todo-workbench-section-header h3 {
  margin: 0 0 6px;
  font-size: 16px;
  font-weight: 600;
  color: var(--text-main);
}

.todo-workbench-section-header p {
  margin: 0;
  font-size: 13px;
  color: var(--text-muted);
}

.todo-workbench-cards {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
}

.todo-workbench-card {
  background: var(--surface-card);
  border-radius: var(--radius-lg);
  padding: 20px;
  border: 1px solid var(--border-color);
  border-left: 4px solid var(--accent);
  box-shadow: var(--shadow-sm);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.todo-workbench-card:hover {
  box-shadow: var(--shadow-md);
  transform: translateY(-2px);
  border-color: var(--accent);
}

.todo-workbench-card-approval {
  background: linear-gradient(180deg, #FFFBEB 0%, var(--surface-card) 100%);
}

.todo-workbench-card-label {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-muted);
  margin-bottom: 8px;
}

.todo-workbench-card-value {
  font-size: 36px;
  font-weight: 700;
  color: var(--accent);
  line-height: 1.1;
  margin-bottom: 8px;
}

.todo-workbench-card-hint {
  font-size: 12px;
  color: #9CA3AF;
}

.stat-card {
  background: var(--surface-card);
  border-radius: var(--radius-lg);
  padding: 20px;
  box-shadow: var(--shadow-sm);
  border: 1px solid var(--border-color);
  border-left: 4px solid var(--accent);
  transition: all var(--transition-fast);
}

.stat-card:hover {
  box-shadow: var(--shadow-md);
  transform: translateY(-2px);
}

.stat-label {
  font-size: 13px;
  color: var(--text-muted);
  font-weight: 500;
  margin-bottom: 8px;
}

.stat-value {
  font-size: 28px;
  font-weight: 700;
  color: var(--text-main);
  line-height: 1.2;
  margin-bottom: 4px;
}

.stat-change {
  font-size: 12px;
  min-height: 18px;
  margin-bottom: 4px;
}

.change-good {
  color: var(--success);
}

.change-bad {
  color: var(--danger);
}

.stat-sub {
  font-size: 11px;
  color: #9CA3AF;
}

.charts-row {
  display: grid;
  grid-template-columns: 3fr 2fr;
  gap: 20px;
  margin-bottom: 24px;
}

.chart-card {
  background: var(--surface-card);
  border-radius: var(--radius-lg);
  padding: 20px;
  box-shadow: var(--shadow-sm);
  border: 1px solid var(--border-color);
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.chart-header h3 {
  font-size: 15px;
  font-weight: 600;
  color: var(--text-main);
  margin: 0;
}

.chart-tabs {
  display: flex;
  gap: 4px;
}

.btn-tab {
  background: #fff;
  color: var(--text-muted);
  border: 1px solid var(--border-color);
  padding: 4px 12px;
  font-size: 12px;
  border-radius: var(--radius-sm);
}

.btn-tab:hover {
  color: var(--primary);
  border-color: var(--primary);
}

.btn-tab.active {
  background: var(--primary);
  color: #fff;
  border-color: var(--primary);
}

.chart-container {
  width: 100%;
  height: 300px;
}

.action-row {
  display: block;
}

.todo-workbench-dialog {
  max-height: 88vh;
  display: flex;
  flex-direction: column;
}

.todo-workbench-approval {
  border-top: 4px solid #F59E0B;
}

.todo-workbench-payment {
  border-top: 4px solid #F97316;
}

.todo-workbench-verify {
  border-top: 4px solid #06B6D4;
}

.todo-workbench-subtitle {
  margin: 6px 0 0;
  font-size: 13px;
  color: var(--text-muted);
  font-weight: 400;
}

.todo-workbench-list {
  max-height: min(52vh, 520px);
  overflow-y: auto;
  margin-top: 4px;
}

.pending-workbench-item {
  align-items: stretch;
  gap: 10px;
  cursor: default;
}

.quick-actions {
  background: var(--surface-card);
  border-radius: var(--radius-lg);
  padding: 20px;
  box-shadow: var(--shadow-sm);
  border: 1px solid var(--border-color);
}

.batch-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  margin-bottom: 12px;
  border-radius: var(--radius-md);
  background: #FFF7ED;
  border: 1px solid #FED7AA;
}

.selection-count {
  color: #9A3412;
  font-size: 13px;
  font-weight: 600;
}

.batch-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.btn-batch,
.btn-row {
  border-radius: var(--radius-sm);
  border: 1px solid var(--border-color);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.btn-batch {
  padding: 7px 12px;
  font-size: 12px;
  font-weight: 600;
}

.btn-batch-light {
  background: #fff;
  color: var(--text-main);
}

.btn-batch-primary {
  background: #F59E0B;
  border-color: #F59E0B;
  color: #fff;
}

.btn-batch:hover:not(:disabled),
.btn-row:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: var(--shadow-sm);
}

.btn-batch:disabled,
.btn-row:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.empty-text {
  color: var(--text-muted);
  font-size: 14px;
  text-align: center;
  padding: 24px 0;
}

.pending-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: background var(--transition-fast);
}

.pending-item:hover {
  background: var(--primary-light);
}

.pending-approval-item {
  align-items: stretch;
  gap: 10px;
  cursor: default;
}

.pending-approval-item:hover,
.pending-workbench-item:hover {
  background: #F9FAFB;
}

.pending-approval-item.selected {
  background: #FFF7ED;
  box-shadow: inset 3px 0 0 #F59E0B;
}

.pending-item + .pending-item {
  border-top: 1px solid var(--border-color);
}

.pending-select {
  display: flex;
  align-items: center;
}

.pending-select input {
  width: 16px;
  height: 16px;
  accent-color: #F59E0B;
}

.pending-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
}

.pending-name {
  font-weight: 600;
  font-size: 14px;
  color: var(--text-main);
}

.pending-activity {
  font-size: 12px;
  color: var(--text-muted);
}

.pending-time,
.pending-therapist {
  font-size: 12px;
  color: #92400E;
}

.pending-meta {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  min-width: 90px;
}

.pending-date {
  font-size: 12px;
  color: var(--text-muted);
}

.pending-amount {
  font-weight: 600;
  font-size: 14px;
  color: var(--primary);
}

.pending-row-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  justify-content: flex-end;
  min-width: 210px;
}

.btn-row {
  background: #fff;
  color: var(--text-main);
  padding: 5px 8px;
  font-size: 12px;
}

.btn-row-primary {
  background: #F59E0B;
  border-color: #F59E0B;
  color: #fff;
}

.btn-row-danger {
  color: var(--danger);
  border-color: #FECACA;
}

.btn-view-all {
  display: block;
  width: 100%;
  margin-top: 12px;
  background: var(--primary-light);
  color: var(--primary);
  border: none;
  padding: 8px;
  border-radius: var(--radius-md);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  text-align: center;
  transition: all var(--transition-fast);
}

.btn-view-all:hover {
  background: var(--primary);
  color: #fff;
}

.quick-actions h3 {
  font-size: 15px;
  font-weight: 600;
  color: var(--text-main);
  margin: 0 0 16px 0;
}

.action-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.action-btn {
  background: var(--primary-light);
  color: var(--primary);
  border: 1px solid transparent;
  padding: 12px 16px;
  border-radius: var(--radius-md);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all var(--transition-fast);
  text-align: center;
}

.action-btn:hover {
  background: var(--primary);
  color: #fff;
  transform: translateY(-1px);
}

.loading-state {
  text-align: center;
  padding: 60px 0;
  color: var(--text-muted);
  font-size: 15px;
}

.error-state {
  text-align: center;
  padding: 40px 0;
  color: var(--danger);
  font-size: 14px;
}

.dialog-mask {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(15, 23, 42, 0.42);
}

.dialog {
  width: min(520px, 100%);
  max-height: 86vh;
  overflow-y: auto;
  background: #fff;
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
  padding: 20px;
}

.dialog-wide {
  width: min(860px, 100%);
}

.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
}

.dialog-header h3 {
  margin: 0;
  font-size: 16px;
  color: var(--text-main);
}

.dialog-close {
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 999px;
  background: var(--surface-muted);
  color: var(--text-muted);
  cursor: pointer;
}

.dialog-notes {
  width: 100%;
  min-height: 84px;
  box-sizing: border-box;
  padding: 10px 12px;
  border: 1px solid var(--border-color);
  border-radius: var(--radius-md);
  resize: vertical;
  font: inherit;
}

.batch-summary {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin: 12px 0;
  padding: 10px 12px;
  border-radius: var(--radius-md);
  background: #F9FAFB;
  font-size: 13px;
  font-weight: 600;
}

.summary-success {
  color: var(--success);
}

.summary-failed {
  color: var(--danger);
}

.preview-groups {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  margin-top: 12px;
}

.preview-group {
  border: 1px solid var(--border-color);
  border-radius: var(--radius-md);
  overflow: hidden;
}

.preview-group h4 {
  margin: 0;
  padding: 10px 12px;
  background: #F9FAFB;
  font-size: 13px;
}

.preview-empty {
  padding: 16px 12px;
  color: var(--text-muted);
  font-size: 13px;
}

.preview-row {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 12px;
  font-size: 13px;
}

.preview-row + .preview-row {
  border-top: 1px solid var(--border-color);
}

.preview-row strong,
.preview-row span {
  display: block;
}

.preview-row strong {
  color: var(--text-main);
}

.preview-meta {
  text-align: right;
  color: var(--text-muted);
}

.room-name {
  color: var(--success);
  font-weight: 600;
}

.failed-reason {
  color: var(--danger);
  font-weight: 600;
}

.preview-row-failed {
  background: #FEF2F2;
}

.decision-summary {
  padding: 10px 12px;
  margin-bottom: 12px;
  border-radius: var(--radius-md);
  background: #F9FAFB;
  color: var(--text-main);
  font-size: 13px;
}

.decision-summary p {
  margin: 3px 0;
}

.dialog-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 16px;
}

@media (max-width: 1400px) {
  .stats-grid {
    grid-template-columns: repeat(4, 1fr);
  }
}

@media (max-width: 1200px) {
  .stats-grid {
    grid-template-columns: repeat(3, 1fr);
  }
  .todo-workbench-cards {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 900px) {
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  .charts-row {
    grid-template-columns: 1fr;
  }
  .pending-approval-item,
  .preview-groups {
    grid-template-columns: 1fr;
  }
  .pending-row-actions {
    min-width: 100%;
    justify-content: flex-start;
  }
}

@media (max-width: 600px) {
  .stats-grid {
    grid-template-columns: 1fr;
  }
  .filter-row {
    flex-direction: column;
    align-items: stretch;
  }
  .filter-item select,
  .date-inputs-inline input[type="date"] {
    min-width: unset;
  }
}
</style>